import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, Renderer2, ViewEncapsulation} from '@angular/core';
import {ngxUiTourAppTours} from '@shared/constants/ngx-ui-tour.constants';
import {NgxUiTourStep} from '@shared/models/ngx-ui-tour.model';
import {AppConfigService} from '@shared/services/app-config.service';
import {NgxUiTourService} from '@shared/services/ngx-ui-tour.service';
import {PermissionService} from '@shared/services/permission.service';
import {TourService} from 'ngx-ui-tour-ngx-bootstrap';
import {Subscription} from 'rxjs';

@Component({
  selector: 'si-ngx-ui-tour',
  templateUrl: './ngx-ui-tour.component.html',
  styleUrls: ['./ngx-ui-tour.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class NgxUiTourComponent implements OnInit, OnDestroy {
  @Input() tourName = 'tourName';
  @Input() steps: NgxUiTourStep[];
  @Input() stepDefaults: NgxUiTourStep;
  @Output() startedTour = new EventEmitter<any>();
  @Output() endTourCompleted = new EventEmitter<any>();
  @Output() endTourUncompleted = new EventEmitter<any>();
  started: boolean;
  subscriptions: Subscription[] = [];
  backdropClickListener: () => void;
  stepsLength: number;
  currentStepNumber: number;

  constructor(
    private tourService: TourService,
    private ngxUiTourService: NgxUiTourService,
    private renderer: Renderer2,
    private appConfigService: AppConfigService,
    private permissionService: PermissionService
  ) { }

  ngOnInit(): void {
    if (this.ngxUiTourService.isTourStarted() || this.ngxUiTourService.isTourCompleted(this.tourName)) {
      return;
    }

    this.initialize();
  }
  
  async initialize(): Promise<void> {
    await this.initTour();
    this.setTourStepLogic();
  }

  async initTour(): Promise<void> {
    if (!this.stepDefaults) {
      this.setTourStepDefaults();
    }

    if (!this.steps) {
      await this.setTourSteps();
    }

    this.filterSteps();
    this.tourService.initialize(this.steps, this.stepDefaults);
    this.startTour();
  }

  setTourStepDefaults(): void {
    this.stepDefaults = this.ngxUiTourService.getTourStepDefaults();
  }

  async setTourSteps(): Promise<void> {
    switch (this.tourName) {
      case ngxUiTourAppTours.HOME_TOUR:
        this.steps = this.ngxUiTourService.getTourStepsHome();
        break;
      case ngxUiTourAppTours.MENU_TOUR:
        this.steps = await this.ngxUiTourService.getTourStepsMenu();
        break;
      case ngxUiTourAppTours.PROFILE_TOUR:
        this.steps = this.ngxUiTourService.getTourStepsProfile();
        break;
      case ngxUiTourAppTours.DIARY_TOUR:
        this.steps = this.ngxUiTourService.getTourStepsDiary();
        break;
      default:
    }
  }

  filterSteps(): void {
    this.filterStepsByPermissions();
    this.filterStepsByConfigurationKeys();
    this.filterStepsByHide();
  }

  // On step change
  setTourStepLogic(): void {
    this.subscriptions.push(
      this.tourService.stepShow$.subscribe((step: NgxUiTourStep) => {
        this.stepsLength = this.getStepsLength();
        this.currentStepNumber = this.getCurrentStepNumber();
        this.addBackdropClickListener();
        this.checkCenterTourElement(step);
        this.checkTourAnchorBorder(step);
      })
    );
  }

  // Backdrop element click detector logic
  addBackdropClickListener(): void {
    this.removeBackdropClickListener();
    const backdropElement = document.querySelector('.ngx-ui-tour_backdrop') as HTMLElement;
    if (!backdropElement) {
      return;
    }

    backdropElement.classList.add('outsideCloseNavSidebar');
    this.backdropClickListener = this.renderer.listen(backdropElement, 'click', () => {
      // this.hasNext() ? this.tourService.next() : this.tourService.end();
    });
  }

  removeBackdropClickListener(): void {
    if (this.backdropClickListener) {
      this.backdropClickListener();
      this.backdropClickListener = null;
    }
  }

  // Check center tour element -> hide arrow
  checkCenterTourElement(step: NgxUiTourStep): void {
    const isCenterTourElement = step.containerClass?.includes('ngx-ui-tour_centered-tour-element');
    if (!isCenterTourElement) {
      return;
    }

    this.hidePopoverArrow();
  }

  hidePopoverArrow(): void {
    const popoverArrow = document.querySelector('.popover-arrow.arrow') as HTMLElement;
    if (!popoverArrow) {
      return;
    }

    this.renderer.setStyle(popoverArrow, 'display', 'none');
  }

  // Check enable border -> add border
  checkTourAnchorBorder(step: NgxUiTourStep): void {
    const enableBorder = step.enableBorder;
    if (!enableBorder) {
      return;
    }

    this.addTourAnchorBorder();
  }

  addTourAnchorBorder(): void {
    setTimeout(() => {
      const touranchorElement = document.querySelector('.touranchor--is-active');
      this.renderer.addClass(touranchorElement, 'ngx-ui-tour_border-black');
    });
  }

  // Basic Tour methods logic
  onClickPrev(): void {
    this.tourService.prev();
  }

  onClickNext(): void {
    this.tourService.next();
  }

  onClickEnd(): void {
    this.endTour(true);
  }

  onClickStart(): void {
    this.tourService.next();
  }

  onClickCancel(): void {
    this.endTour(true);
  }

  startTour(): void {
    this.started = true;
    this.ngxUiTourService.setTourName(this.tourName);
    this.startedTour.emit();
    // Wait HTML update and then start Tour
    setTimeout(() => {
      this.tourService.start();
    }, 200);
  }

  endTour(tourCompleted: boolean): void {
    if (!this.started) {
      return;
    }

    this.tourService.end();
    this.started = false;
    tourCompleted ? this.completeTour() : this.uncompleteTour();
    this.ngxUiTourService.setTourName(null);
  }

  completeTour(): void {
    this.ngxUiTourService.setTourCompleted();
    this.endTourCompleted.emit();
  }

  uncompleteTour(): void {
    this.endTourUncompleted.emit();
  }

  hasPrev(step = this.tourService.currentStep): boolean {
    return this.tourService.hasPrev(step);
  }

  hasNext(step = this.tourService.currentStep): boolean {
    return this.tourService.hasNext(step);
  }

  getCurrentStepNumber(step = this.tourService.currentStep): number {
    return this.tourService.steps?.indexOf(step) + 1;
  }

  getStepsLength(): number {
    return this.tourService.steps?.length;
  }

  // Steps management functions
  filterStepsByPermissions(): void {
    this.steps = this.permissionService.filterItemsByPermissions(this.steps);
  }

  filterStepsByConfigurationKeys(): void {
    this.steps = this.appConfigService.filterItemsByConfigurations(this.steps);
  }

  filterStepsByHide(): void {
    this.steps = this.steps.filter(step => !step.hide);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
    this.removeBackdropClickListener();
    this.endTour(false);
  }
}
